<script>
import DynamicAppFeedElement
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppFeedElement.vue";
import FeedV2Selection from "%/dynamic-component-editor/options/feed-v2-selection";
import ProductLinkCollection from "%/dynamic-component-editor/options/product-link-collection";
import { resolveContextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import AppFeedOptions from "%/dynamic-component-editor/bundles/app-feed-options";
import FeedEmptyStateOptions from "%/dynamic-component-editor/bundles/feed-empty-state-options";
import FeedPlaybackOptions from "%/dynamic-component-editor/bundles/feed-playback-options";
import FeedSeparatorsOptions from "%/dynamic-component-editor/bundles/feed-separators-options";
import FeedFiltersOptions from "%/dynamic-component-editor/bundles/feed-filters-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import FeedLayoutOptions from "%/dynamic-component-editor/bundles/feed-layout-options";
import FeedDesignOptions from "%/dynamic-component-editor/bundles/feed-design-options";
import {
  PLAN_SHEET_INJECTION_KEY,
  SUBSCRIPTION_PLANS_FEED_FIELD,
  buildPlanRowBindings,
  filterVisiblePlans,
} from "%/dynamic-component-editor/standard-components/plan-sheet/plan-sheet-state";

const PUBLICATION_APP_NAME = "CreatorStudio";
const PUBLICATION_ENTITY_TYPE = "Publication";
const PRODUCTS_FEED_FIELD = "products";
const COLLECTION_FILTER_NAME = "collectionId";

export default {
  name: "FeedV2Element",
  extends: DynamicAppFeedElement,
  elementPanelSettings: {
    tags: ["feed", "list", "grid", "stories"],
    displayName: "FeedV2",
    elementGroup: "general",
    displayIcon: "feed",
    elementType: "element",
  },
  context: {
    appName: PUBLICATION_APP_NAME,
    entityContext: PUBLICATION_ENTITY_TYPE,
  },
  optionBundles: [
    new AppearanceOptions({}, {}, false),
    new AppFeedOptions({}, [ProductLinkCollection]),
    new FeedSeparatorsOptions(),
    new FeedFiltersOptions(),
    new FeedEmptyStateOptions(),
    new FeedPlaybackOptions(),
    new SpacingOptions(),
    new FeedLayoutOptions(),
    new FeedDesignOptions(),
  ],
  options: [
    FeedV2Selection,
  ],
  hidden: false,
  inject: {
    planSheet: { from: PLAN_SHEET_INJECTION_KEY, default: null },
  },
  computed: {
    contextAppName() {
      return resolveContextualAppName(PUBLICATION_APP_NAME);
    },
    entityType() {
      return PUBLICATION_ENTITY_TYPE;
    },
    pinnedProductCollectionId() {
      if (this.fieldName !== PRODUCTS_FEED_FIELD) {
        return 0;
      }
      const picked = this.getOptionValue(ProductLinkCollection);
      return Number(picked?.value ?? picked) || 0;
    },
    activeFilters() {
      const hashtagFilters = DynamicAppFeedElement.computed.activeFilters.call(this);
      if (!this.pinnedProductCollectionId) {
        return hashtagFilters;
      }
      return { ...hashtagFilters ?? {}, [COLLECTION_FILTER_NAME]: `|${this.pinnedProductCollectionId}|` };
    },
    publicationId() {
      return useConfigurationStore().publicationId
        || (window.blclient?.data?.themeDestinationId ?? 0);
    },
    contextItem() {
      return {
        entityType: PUBLICATION_ENTITY_TYPE,
        entityPkUrlVars: { id: this.publicationId },
      };
    },
    fieldName() {
      return this.getOptionValue(FeedV2Selection) || null;
    },
    feedContext() {
      if (!this.fieldName || !this.publicationId) {
        return {};
      }
      return {
        entityType: PUBLICATION_ENTITY_TYPE,
        pk: { id: this.publicationId },
      };
    },
    isPlanSheetFeed() {
      return Boolean(this.planSheet) && !this.editMode && this.fieldName === SUBSCRIPTION_PLANS_FEED_FIELD;
    },
    loadedRows() {
      return DynamicAppFeedElement.computed.entities.call(this);
    },
    loadedPlans() {
      if (!this.isPlanSheetFeed) {
        return [];
      }
      return this.loadedRows.map(row => this.planForRow(row)).filter(Boolean);
    },
    entities() {
      if (!this.isPlanSheetFeed) {
        return this.loadedRows;
      }
      const visibleIds = new Set(filterVisiblePlans(this.loadedPlans, this.planSheet.interval).map(plan => plan.id));
      return this.loadedRows.filter(row => visibleIds.has(Number(row.entityPk.id)));
    },
  },
  created() {
    this.readSheetPlans = () => this.loadedPlans;
    this.planSheet?.addPlanSource(this.readSheetPlans);
  },
  beforeUnmount() {
    this.planSheet?.removePlanSource(this.readSheetPlans);
  },
  methods: {
    planForRow(row) {
      const entity = this.feedApp?.getEntity(this.rowEntityType, row.entityPk);
      if (!entity) {
        return null;
      }
      return {
        id: Number(row.entityPk.id),
        name: entity.name,
        price: entity.price,
        billingFrequency: entity.billingFrequency,
        currencyCode: entity.currencyCode,
        planPrices: entity.planPrices ?? [],
      };
    },
    rowBindings(entity) {
      if (!this.isPlanSheetFeed) {
        return DynamicAppFeedElement.methods.rowBindings.call(this);
      }
      const planId = Number(entity.entityPk.id);
      return buildPlanRowBindings(this.planSheet.selectedPlanId === planId, () => this.planSheet.selectPlan(planId));
    },
  },
};
</script>
